/**
 * Auth keys are stored in localStorage so they are shared across browser tabs.
 * Tab-specific non-auth state (redirectAfterLogin, pendingInvitation, etc.)
 * stays in sessionStorage as before.
 */

import { COGNITO_CLIENT_ID, COGNITO_DOMAIN } from '../config';

const AUTH_KEYS = [
  'idToken',
  'accessToken',
  'refreshToken',
  'tenantId',
  'userId',
  'userEmail',
  'userName',
  'userRole',
  'orgName',
] as const;

/**
 * Tell Cognito to invalidate the refresh token server-side.
 *
 * Added 2026-08-04 (security review, hygiene). Removing the key from localStorage only forgets
 * our copy — the token itself stayed valid for its full 30-day window, so anything that had
 * already read it out of localStorage (XSS, a shared machine, a browser extension) kept working
 * long after the user believed they had signed out. Revoking it also invalidates the access
 * tokens issued from it.
 *
 * Deliberately fire-and-forget:
 *
 *  - `clearAuthStorage` stays synchronous. Its callers include a response interceptor
 *    (`services/api.ts`) and an inactivity timer — neither can await, and clearing local state
 *    must never be delayed or blocked by a network round-trip.
 *  - `keepalive` is load-bearing, not decoration. Every caller either navigates immediately
 *    (`window.location.href = …`) or is already unwinding a failed request; a plain fetch would
 *    be cancelled on unload and the token would silently survive.
 *  - Failures are swallowed. If the endpoint cannot be reached the user must still be signed out
 *    locally, which is what the caller does next regardless.
 *
 * No Authorization header is needed: the refresh token in the body *is* the credential. Requires
 * `EnableTokenRevocation` on the app client, which is on.
 */
function revokeRefreshToken(refreshToken: string): void {
  try {
    void fetch(`https://${COGNITO_DOMAIN}/oauth2/revoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ token: refreshToken, client_id: COGNITO_CLIENT_ID }),
      keepalive: true,
    }).catch(() => {
      /* Best effort — see above. */
    });
  } catch {
    /* Never let sign-out fail because revocation could not even be dispatched. */
  }
}

/** Remove all auth keys from localStorage and clear sessionStorage. */
export function clearAuthStorage(): void {
  // Read before removing: the revoke call needs the token this loop is about to delete.
  const refreshToken = localStorage.getItem('refreshToken');
  if (refreshToken) revokeRefreshToken(refreshToken);

  AUTH_KEYS.forEach((key) => localStorage.removeItem(key));
  sessionStorage.clear();
}
